import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WaterBodyDAO } from '../../../../../core/services/dao/waterBodyDAO';
import { UploadRequest } from '../../../../shared/upload/upload-single-photo/upload-single-photo.component';
import { WaterBody } from 'anchor-shared/lib/model/entity/waterBody';
import { UploadDAO } from '../../../../../core/services/dao/uploadDAO';
import { CropperSettings } from 'ngx-img-cropper';
import * as uuid from 'uuid';
import { KebabCasePipe } from '../../../../shared/pipes/kebabCase.pipe';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs/Rx';
import { of, Subscription } from 'rxjs';
import unitedStates from '../../../../../assets/states/united-states';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { AppMessagingService } from '../../../../../core/services/app-messaging.service';
import { ContainsSubscriptions } from '../../../../../core/services/interfaces/contains-subscriptions';
import { AppLocation } from '../../../../../core/models/interface/geo';
import { MarketDAO } from '../../../../../core/services/dao/marketDAO';
import { Market } from 'anchor-shared/lib/model/entity/market';

@Component({
  selector: 'app-add-water-bodies',
  templateUrl: './add-water-bodies.component.html',
  styleUrls: ['./add-water-bodies.component.scss'],
})
export class AddWaterBodiesComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  @Output() onCancelled = new EventEmitter<boolean>();
  @Output() onSubmitted = new EventEmitter<any>();

  public name: string;
  public newWaterBody: Partial<WaterBody> = {};
  public states: any[];
  cropperSettings: CropperSettings;
  featuredBodies: Partial<WaterBody>[] = [];
  public draggedOverIndex: number;
  private draggedIndex: number;
  previewDisplayText: string;
  newUuid: string;
  templateData$: Observable<any>;
  isEdit = false;
  waterBodyIsFeatured = false;
  timezones: string[] = timezoneSelections;
  markets: Market[] = [];
  subs: Subscription[] = [];
  location: AppLocation;
  locationValid = false;
  duplicatesFound = false;
  zoom = 11;
  touched = false;

  constructor(
    private router: Router,
    private uploadDAO: UploadDAO,
    private waterBodyDAO: WaterBodyDAO,
    private marketDAO: MarketDAO,
    private kebabCasePipe: KebabCasePipe,
    private route: ActivatedRoute,
    private appMessagingService: AppMessagingService,
  ) {
    this.states = unitedStates;
  }

  ngOnInit() {
    this.initCropperSettings();

    const sub = this.route.data.subscribe((data) => {
      if (data.isEdit) {
        this.isEdit = true;
        this.locationValid = true;
        this.initAsyncData();
      } else {
        this.templateData$ = of(true);
      }
    });
    this.subs.push(sub);

    const wbSub = this.waterBodyDAO
      .getFeaturedBodies()
      .subscribe((waterBodies) => {
        waterBodies.forEach((waterBody) => {
          this.featuredBodies.push(waterBody);
        });
        this.featuredBodies.sort(function (a, b) {
          return a.featuredMarket - b.featuredMarket;
        });
        if (!this.isEdit) {
          this.featuredBodies.unshift(this.newWaterBody);
        } else {
          if (this.newWaterBody && this.newWaterBody.featuredMarket > 0) {
            this.featuredBodies.push(this.newWaterBody);
          }
        }
        this.marketDAO.getAllMarkets().subscribe((markets) => {
          this.markets = markets;
        });
      });
    this.subs.push(sub, wbSub);
    this.newUuid = uuid.v4();
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return !this.touched;
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  public cancel() {
    this.router.navigate(['manage/admin/water-bodies']);
  }

  public submit() {
    this.newWaterBody.displayName = this.newWaterBody.name;
    if (!this.newWaterBody.activeMarket) {
      this.newWaterBody.featuredMarket = null;
    }

    if (this.isEdit) {
      if (!this.waterBodyIsFeatured) {
        this.newWaterBody.featuredMarket = null;
      }
      const sub = this.waterBodyDAO
        .updateFromPartial(this.newWaterBody.id, this.newWaterBody)
        .subscribe((data) => {
          this.updateMultipleFeaturedMarkets();
          this.router.navigate(['manage/admin/water-bodies']);
        });
      this.subs.push(sub);
    } else {
      const sub = this.waterBodyDAO
        .createWaterBody(this.newWaterBody)
        .subscribe((result) => {
          this.newWaterBody.id = result.id;
          this.updateMultipleFeaturedMarkets();
          this.router.navigate(['manage/admin/water-bodies']);
        });
      this.subs.push(sub);
    }
  }

  hasRequiredFields() {
    return (
      this.newWaterBody.marketId != null &&
      !!this.newWaterBody.timezone &&
      !!this.newWaterBody.displayCity &&
      !!this.newWaterBody.vanityUrl &&
      this.locationValid
    );
  }

  featuredRadioChange(): void {
    this.formTouched();
    if (this.waterBodyIsFeatured) {
      this.newWaterBody.activeMarket = true;
      if (
        !this.isEdit &&
        (!this.newWaterBody.featuredMarket ||
          this.newWaterBody.featuredMarket! > 0)
      ) {
        this.newWaterBody.featuredMarket = 1;
      } else if (this.isEdit) {
        if (!this.existsAsAFeaturedBody()) {
          this.featuredBodies.unshift(this.newWaterBody);
        }
        this.newWaterBody.featuredMarket = 1;
      }
    }
  }

  updateMultipleFeaturedMarkets(): void {
    if (this.newWaterBody.featuredMarket > 0) {
      const sub = this.waterBodyDAO
        .updateMultipleFeaturedMarkets(this.featuredBodies)
        .subscribe((result2) => {});
      this.subs.push(sub);
    }
  }

  nameChange(change: string): void {
    if (change) {
      this.newWaterBody.vanityUrl = this.kebabCasePipe.transform(change);
      this.previewDisplayText = change.toUpperCase();
    }
  }

  onPhotoUploaded(uploadRequest: UploadRequest): void {
    const sub = this.uploadDAO
      .uploadWaterBodyImage(uploadRequest.fileInput, uploadRequest.filename)
      .subscribe((uploadResponse) => {
        this.newWaterBody.fullImagePhotoPath = uploadResponse.filepath;
        this.newWaterBody.metaImagePath = uploadResponse.filepath;
        this.appMessagingService.openSnack('Photo successfully uploaded.');
      });
    this.subs.push(sub);
  }

  private existsAsAFeaturedBody(): boolean {
    return this.featuredBodies.some((e) => e.id === this.newWaterBody.id);
  }

  private initCropperSettings(): void {
    this.cropperSettings = new CropperSettings();
    this.cropperSettings.width = 1200;
    this.cropperSettings.height = 800;
    this.cropperSettings.croppedWidth = 600;
    this.cropperSettings.croppedHeight = 400;
    this.cropperSettings.canvasWidth = 600;
    this.cropperSettings.canvasHeight = 400;
    this.cropperSettings.minWidth = 600;
    this.cropperSettings.minHeight = 400;
    this.cropperSettings.noFileInput = true;
    this.cropperSettings.fileType = 'image/jpeg';
  }

  showUploader() {
    let uploader = document.getElementById('uploader');
    uploader.style.display = 'block';
  }

  hideUploader() {
    let uploader = document.getElementById('uploader');
    uploader.style.display = 'none';
  }

  allowDrop($event, index): void {
    this.draggedOverIndex = index;
    $event.preventDefault();
  }

  onDragStart(index): void {
    this.draggedIndex = index;
  }

  onDrop($event, index): void {
    this.formTouched();
    $event.preventDefault();
    const bow = this.featuredBodies[this.draggedIndex];
    this.featuredBodies.splice(this.draggedIndex, 1);
    this.featuredBodies.splice(index, 0, bow);
    this.draggedIndex = -1;
    this.draggedOverIndex = -1;
    this.featuredBodies.forEach((item, i) => {
      item.featuredMarket = i + 1;
    });
  }

  private initAsyncData() {
    const sub = this.route.params.subscribe((params) => {
      this.templateData$ = this.waterBodyDAO
        .getByWaterBodyId(params.waterBodyId)
        .pipe(
          map((waterBody) => {
            this.newWaterBody = waterBody;
            this.zoom = waterBody.zoom;
            this.previewDisplayText = this.newWaterBody.name;
            if (this.newWaterBody.featuredMarket > 0) {
              this.waterBodyIsFeatured = true;
            }
            this.setMapLocation(waterBody);
            return { waterBody };
          }),
        );
    });
    this.subs.push(sub);
  }

  autocompleteWaterbodyAddress(location: AppLocation) {
    if (location.addressComponents.length > 0) {
      this.waterBodyDAO
        .findDuplicateWaterbodyByLatLong(
          location.position.lat,
          location.position.lng,
        )
        .subscribe((duplicates) => {
          if (duplicates.length > 0) {
            this.locationValid = false;
            this.duplicatesFound = true;
          } else {
            this.location = location;
            this.newWaterBody.latitude = location.position.lat;
            this.newWaterBody.longitude = location.position.lng;
            this.newWaterBody.zoom = location.zoom;
            this.newWaterBody.displayState = this.findDisplayState(location);
            this.locationValid = true;
          }
        });
    }
  }

  zoomChange(zoomObj) {
    this.newWaterBody.zoom = zoomObj.zoom;
    this.zoom = zoomObj.zoom;
  }

  updateLatLongZoom(mapObj) {
    this.newWaterBody.latitude = mapObj.center.latitude;
    this.newWaterBody.longitude = mapObj.center.longitude;
  }

  private findDisplayState(location: AppLocation): string {
    let displayText = '';
    if (location && location.addressComponents) {
      location.addressComponents.forEach((comp) => {
        if (comp.types.includes('administrative_area_level_1')) {
          displayText = comp.long_name;
        }
      });
    }
    return displayText;
  }

  private setMapLocation(waterBody?: WaterBody): void {
    this.location = {
      position: {
        lat: 27.964157,
        lng: -82.452606,
      },
      addressComponents: [],
      formattedAddress: '',
      vicinity: '',
      markerLatitude: 0,
      markerLongitude: 0,
      zoom: 12,
    };
    if (waterBody && waterBody.latitude && waterBody.longitude) {
      this.locationValid = true;
      this.location.mapLatitude = waterBody.latitude;
      this.location.mapLongitude = waterBody.longitude;
      this.location.markerLatitude = waterBody.latitude;
      this.location.markerLongitude = waterBody.longitude;
      this.location.zoom = waterBody.zoom;
    }
  }

  waterBodyContentChanged(ckeditString: string) {
    this.formTouched();
    this.newWaterBody.seoContent = ckeditString;
  }

  formTouched() {
    this.touched = true;
  }
}

export const timezoneSelections = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
];
